import { useState, useRef, useCallback, useMemo, useContext, useEffect } from 'react';
import { debounce } from 'lodash';
import { EntityRecord, ValidationStatus } from '#types/System.types';
import { RecordStatus, DEFAULT_NEW_RECORD_NAME } from '#types/System.Parameters.types';
import { useEntityRecordScope } from '../store';
import { useSaveEntityReference } from './useSaveEntityReference';
import { pendingSavesActions } from '../store/pendingSavesStore';
import { MessageContext } from '../providers/MessageContext';
import { syncDocumentToSearch } from '../services/searchSyncService';

type PropertyKey = 'name' | 'status' | 'entityGroupIds';

type PropertyValue<K extends PropertyKey> = 
  K extends 'name' ? string :
  K extends 'status' ? RecordStatus :
  K extends 'entityGroupIds' ? string[] :
  never;

interface PropertySyncer<T> {
  localValue: T;
  setValue: (value: T) => void;
  clearDirty: () => void;
  isDirty: boolean;
}

interface PropertyState {
  name: PropertySyncer<string>;
  status: PropertySyncer<RecordStatus>;
  entityGroupIds: PropertySyncer<string[]>;
}

interface UseEntityPropertiesReturn {
  properties: PropertyState;
  propertyValidation: Record<string, ValidationStatus>;
  handlePropertyChange: <K extends PropertyKey>(
    propertyName: K,
    value: PropertyValue<K>
  ) => void;
  syncNameToSearch: () => void;
  entityDocId: string | undefined;
  entityLabel: string | undefined;
  entityLocked: boolean;
  hasData: boolean;
}

const arrayEquals = (a: string[], b: string[]): boolean =>
  a.length === b.length && a.every((val, idx) => val === b[idx]);

/**
 * Hook for syncing a single property with local state and dirty tracking.
 * Prevents race conditions during user editing while syncing external changes.
 * 
 * @param externalValue - The value from Firestore/context
 * @param isEqual - Optional equality function for complex types
 * @returns Property syncer with local state and dirty tracking
 */
const usePropertySync = <T>(
  externalValue: T,
  isEqual?: (a: T, b: T) => boolean
): PropertySyncer<T> => {
  const [state, setState] = useState<{
    localValue: T;
    lastExternal: T;
    isDirty: boolean;
  }>(() => ({
    localValue: externalValue,
    lastExternal: externalValue,
    isDirty: false
  }));

  const equalityCheck = isEqual 
    ? isEqual(externalValue, state.lastExternal)
    : externalValue === state.lastExternal;

  if (!equalityCheck && !state.isDirty) {
    setState({
      localValue: externalValue,
      lastExternal: externalValue,
      isDirty: false
    });
  } else if (!equalityCheck) {
    setState(prev => ({
      ...prev,
      lastExternal: externalValue
    }));
  }

  const setValue = useCallback((value: T) => {
    setState(prev => ({
      ...prev,
      localValue: value,
      isDirty: true
    }));
  }, []);

  const clearDirty = useCallback(() => {
    setState(prev => ({
      ...prev,
      isDirty: false
    }));
  }, []);

  return useMemo(() => ({ 
    localValue: state.localValue, 
    setValue, 
    clearDirty,
    isDirty: state.isDirty
  }), [state.localValue, state.isDirty, setValue, clearDirty]);
};

/**
 * Hook for managing EntityRecord properties with local state, dirty tracking,
 * and Firestore persistence via EntityRecordProvider. Uses the FieldUI pattern
 * to avoid context updates and prevent re-renders of other components.
 * Updates both Firestore and EntityRecordsRegistry cache through EntityRecordProvider.
 *
 * @returns Property state, handlers, and minimal render data
 *
 * @example
 * const { properties, handlePropertyChange, entityLabel } = useEntityProperties();
 *
 * <Input
 *   value={properties.name.localValue}
 *   onChange={(e) => handlePropertyChange('name', e.target.value)}
 * />
 */
export const useEntityProperties = (): UseEntityPropertiesReturn => {
  const { messageApi } = useContext(MessageContext);
  const scope = useEntityRecordScope();
  const saveEntityReference = useSaveEntityReference();

  const entity = scope.entity.data;
  const entityRecord = scope.entityRecord.data;
  const entityLocked = scope.entityLocked;

  const entityRecordName = useMemo(() => entityRecord?.name || '', [entityRecord?.name]);
  const entityRecordStatus = useMemo(() => entityRecord?.status || RecordStatus.Active, [entityRecord?.status]);
  const entityRecordGroupIds = useMemo(() => entityRecord?.entityGroupIds || [], [entityRecord?.entityGroupIds]);
  const entityRecordMeta = useMemo(() => entityRecord?.meta, [entityRecord?.meta]);

  const name = usePropertySync(entityRecordName);
  const status = usePropertySync(entityRecordStatus);
  const entityGroupIds = usePropertySync(entityRecordGroupIds, arrayEquals);

  const properties: PropertyState = useMemo(() => ({
    name,
    status,
    entityGroupIds
  }), [name, status, entityGroupIds]);

  /** Per-save settle timers, keyed by pending-save id so each clears independently of record switches. */
  const settleTimeoutsRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  const propertyValidation = useMemo<Record<string, ValidationStatus>>(() => ({}), []);

  const entityDocId = useMemo(() => entity?.docId, [entity?.docId]);
  const entityLabel = useMemo(
    () => entity?.description?.shortLabel || entity?.docId,
    [entity?.description?.shortLabel, entity?.docId]
  );
  const hasData = useMemo(
    () => !!(entity && entityRecord),
    [entity, entityRecord]
  );

  const debouncedSyncToSearchRef = useRef(
    debounce((entityId: string, recordId: string) => {
      syncDocumentToSearch('records', entityId, recordId)
        .catch(err => console.warn('[useEntityProperties] Failed to sync record to search:', err));
    }, 1200)
  );

  useEffect(() => {
    const settleTimeouts = settleTimeoutsRef.current;
    const syncDebounced = debouncedSyncToSearchRef.current;
    return () => {
      syncDebounced.flush();
      settleTimeouts.forEach((timeout, pendingId) => {
        clearTimeout(timeout);
        pendingSavesActions.removePending(pendingId);
      });
      settleTimeouts.clear();
    };
  }, []);

  const handlePropertyChange = useCallback(<K extends PropertyKey>(
    propertyName: K,
    value: PropertyValue<K>
  ) => {
    if (entityLocked) return;

    const property = properties[propertyName];
    property.setValue(value as any);

    if (!scope.insideEntityRecordContext) {
      console.error('[useEntityProperties] No entity record scope available');
      messageApi.error('Cannot save: Record context not available');
      return;
    }

    const pendingId = `entityProperty:${scope.entityId}:${scope.recordId}:${propertyName}`;
    pendingSavesActions.addPending(pendingId);

    const updateData: Partial<EntityRecord> = { [propertyName]: value };

    if (propertyName === 'name' && typeof value === 'string') {
      updateData.meta = {
        ...entityRecordMeta,
        searchNameLowercase: value.toLowerCase()
      };
    }

    scope.debouncedSetEntityRecord(updateData);

    // Re-stamp the host form's denormalized entityRecordName so an inline rename
    // stays in sync with the record doc (no-op outside a form context).
    if (propertyName === 'name' && typeof value === 'string') {
      void saveEntityReference(undefined, scope.entityGroupId, undefined, undefined, value);
    }

    // Sync status or entityGroupIds changes to search index
    if ((propertyName === 'status' || propertyName === 'entityGroupIds') &&
        scope.entityId && scope.recordId) {
      debouncedSyncToSearchRef.current(scope.entityId, scope.recordId);
    }

    // Clear the pending flag once the debounced persist write has settled (debounce + network margin).
    const existingTimeout = settleTimeoutsRef.current.get(pendingId);
    if (existingTimeout) clearTimeout(existingTimeout);
    const timeout = setTimeout(() => {
      pendingSavesActions.removePending(pendingId);
      settleTimeoutsRef.current.delete(pendingId);
      property.clearDirty();
    }, 1200);
    settleTimeoutsRef.current.set(pendingId, timeout);
  }, [entityLocked, scope, entityRecordMeta, properties, messageApi, saveEntityReference]);


  const syncNameToSearch = useCallback(() => {
    const currentName = properties.name.localValue;
    if (currentName !== DEFAULT_NEW_RECORD_NAME &&
        scope.entityId &&
        scope.entityRecord.data?.docId) {
      syncDocumentToSearch('records', scope.entityId, scope.entityRecord.data.docId)
        .catch(err => console.warn('[useEntityProperties] Failed to sync record to search:', err));
    }
  }, [properties.name.localValue, scope]);

  return {
    properties,
    propertyValidation,
    handlePropertyChange,
    syncNameToSearch,
    entityDocId,
    entityLabel,
    entityLocked,
    hasData
  };
};

